// config/guestVoiceLabels.js
// «صوت الضيف» — the words for the review read's codes (tasks/guest-voice-tab.md step 5). The API sends codes only
// (be/config/reviewTopics.js, I18N_STRATEGY); every word on the tab lives here. A key added there needs a word here —
// an unknown key falls back to «غير مصنّف», never to the raw code.

import { formatCount } from '../utils/numberFormatter';

const n_ = (n) => formatCount(n);

// Arabic counted nouns: one / two / 3–10 / 11+ each take their own form.
const counted = (n, { one, two, few, many }) => {
  if (n === 1) return one;
  if (n === 2) return two;
  if (n >= 3 && n <= 10) return `${n_(n)} ${few}`;
  return `${n_(n)} ${many}`;
};

// G16 — problem topics: one name per key; the heading comes from the key itself.
export const PROBLEM_TOPIC_LABELS = {
  'maintenance.ac': 'التكييف',
  'maintenance.bathroom_drain': 'تصريف الحمام والسباكة',
  'equipment.wifi': 'الواي فاي والإنترنت',
  'equipment.kitchen_kit': 'أدوات المطبخ وأجهزته',
  'property_condition.smell': 'الرائحة',
  'property_condition.lighting': 'الإضاءة',
  'other.parking': 'المواقف',
  'access.general': 'الدخول إلى الوحدة',
  'cleanliness.general': 'النظافة',
  'equipment.general': 'أجهزة وتجهيزات أخرى',
  'maintenance.general': 'أعطال أخرى',
  'noise.general': 'الإزعاج',
  'property_condition.general': 'حالة الوحدة',
  'housekeeping.general': 'المستلزمات والمناشف',
  'other.general': 'ملاحظات أخرى',
  other: 'غير مصنّف'
};

export const PRAISE_TOPIC_LABELS = {
  cleanliness: 'النظافة',
  hospitality: 'حسن التعامل والاستقبال',
  location: 'الموقع',
  quiet: 'الهدوء',
  comfort: 'الراحة',
  design: 'التصميم والديكور',
  value: 'السعر مقابل القيمة',
  easy_checkin: 'سهولة الدخول',
  as_described: 'مطابقة للإعلان والصور',
  other: 'إشادة عامة'
};

// Airbnb's "what to improve" labels the guest ticked (tasks/airbnb-review-labels.md) — the API sends the code
// (be/config/reviewTopics.js AIRBNB_LABEL_TOPICS), the words live here. A label Airbnb adds later shows the fallback.
export const AIRBNB_LABEL_WORDS = {
  smaller_than_expected: 'أصغر من المتوقع',
  did_not_match_photos: 'لا تطابق الصور',
  needs_maintenance: 'تحتاج إلى صيانة',
  unexpected_fees: 'رسوم غير متوقعة',
  excessive_rules: 'قواعد مبالغ فيها',
  unexpected_noise: 'إزعاج غير متوقع',
  inaccurate_location: 'الموقع غير دقيق',
  missing_amenity: 'مرفق ناقص',
  broken_or_missing_lock: 'قفل معطّل أو مفقود',
  unexpected_guests: 'وجود أشخاص غير متوقعين',
  incorrect_bathroom: 'دورة المياه لا تطابق الوصف',
  hard_to_locate: 'صعوبة العثور على الوحدة',
  unclear_instructions: 'تعليمات الدخول غير واضحة',
  trouble_with_lock: 'مشكلة في القفل',
  unresponsive_host: 'لا رد عند الوصول',
  had_to_wait: 'انتظار عند الوصول',
  hard_to_get_inside: 'صعوبة الدخول إلى الوحدة',
  dirty_or_dusty: 'متسخة أو مغبرة',
  noticeable_smell: 'رائحة ملحوظة',
  stains: 'بقع',
  excessive_clutter: 'فوضى',
  messy_kitchen: 'المطبخ غير نظيف',
  hair_or_pet_hair: 'شعر متروك',
  dirty_bathroom: 'دورة المياه غير نظيفة',
  trash_left_behind: 'القمامة المتروكة',
  slow_to_respond: 'بطء في الرد',
  not_helpful: 'عدم تعاون',
  missing_house_instructions: 'تعليمات الوحدة ناقصة',
  unclear_checkout_tasks: 'مهام المغادرة غير واضحة',
  inconsiderate: 'عدم مراعاة',
  excessive_checkout_tasks: 'مهام مغادرة كثيرة',
  noisy: 'المنطقة مزعجة',
  not_much_to_do: 'لا يوجد الكثير للقيام به',
  bland_surroundings: 'المحيط غير جذاب',
  not_private: 'خصوصية غير كافية',
  inconvenient_location: 'موقع غير مناسب'
};
export const airbnbLabelWords = (code) => AIRBNB_LABEL_WORDS[code] || 'اختيار آخر من قائمة Airbnb';

const UNKNOWN = 'غير مصنّف';
export const problemLabel = (key) => PROBLEM_TOPIC_LABELS[key] || UNKNOWN;
export const praiseLabel = (key) => PRAISE_TOPIC_LABELS[key] || UNKNOWN;

// After «في» the dual is «تقييمين», not «تقييمان» (the nominative `reviewsCount` form).
const reviewsAfterIn = (n) => counted(n, { one: 'تقييم واحد', two: 'تقييمين', few: 'تقييمات', many: 'تقييمًا' });

export const TEXT = {
  tabTitle: 'صوت الضيف',
  loading: 'جارٍ تحميل ما قاله الضيوف…',
  loadError: 'تعذّر تحميل صوت الضيف.',
  retry: 'إعادة المحاولة',

  // G14 — never "no problems" for a review nobody has read.
  readStatus: (read, total) => `تمت قراءة ${n_(read)} من ${n_(total)} تقييمًا`,
  notRead: (n) => `${n_(n)} لم تُقرأ بعد، ولا تُحسب ضمن المشاكل حتى تُقرأ`,
  failed: (n) => `${n_(n)} تعذّرت قراءتها، ولا تُحسب ضمن المشاكل`,

  noUnitsTitle: 'لا توجد تقييمات مرتبطة بوحدة بعد',
  noUnitsHint: 'تظهر هنا تقييمات Airbnb وBooking.com بعد ربطها بحجز الوحدة.',
  unitsLabel: 'اختر الوحدة',
  unitsTitle: 'الوحدات',
  // The tile: how many problems, what state they are in, and the most important one.
  problemsCount: (n) => counted(n, { one: 'مشكلة واحدة', two: 'مشكلتان', few: 'مشاكل', many: 'مشكلة' }),
  unitClear: 'لا توجد مشاكل',
  unitHandled: 'قيد المتابعة',
  unitUnlogged: (n) => (n === 1 ? 'واحدة بلا شكوى' : `${n_(n)} بلا شكوى`),
  stateHigh: (n) => (n === 1 ? 'عاجلة' : `${n_(n)} عاجلة`),
  stateRepeat: (n) => (n === 1 ? 'متكررة' : `${n_(n)} متكررة`),
  stateCameBack: (n) => (n === 1 ? 'عادت بعد الإصلاح' : `${n_(n)} عادت بعد الإصلاح`),
  leadTopic: (name) => `أهمها: ${name}`,
  // Units with nothing new (no problem, or every problem already has a complaint) fold into one line.
  foldedUnits: (n) => counted(n, {
    one: 'وحدة واحدة بلا مشاكل جديدة', two: 'وحدتان بلا مشاكل جديدة', few: 'وحدات بلا مشاكل جديدة', many: 'وحدة بلا مشاكل جديدة'
  }),
  foldShow: 'عرض الكل',
  foldHide: 'إخفاء',

  // G20 — one line of orientation above the tiles: the tiles' total, counts only.
  praiseCount: (n) => counted(n, { one: 'إشادة واحدة', two: 'إشادتان', few: 'إشادات', many: 'إشادة' }),
  summaryRepeat: (n) => `${n_(n)} متكررة`,
  summaryHigh: (n) => `${n_(n)} عاجلة`,

  // G23–G26 — the block across units: two single-series bar lists, counts only; the bar is the units (problems)
  // or the reviews (praise); the count beside it is the accessible value.
  fleetTitle: 'في كل الوحدات',
  fleetHint: 'الأكثر ذكرًا في تقييمات الضيوف، بالعدد',
  fleetProblemsEmpty: 'لا مشاكل مذكورة',
  fleetPraiseEmpty: 'لا إشادات بعد',
  topUnits: (n) => (n === 1 ? 'في وحدة واحدة' : n === 2 ? 'في وحدتين' : n <= 10 ? `في ${n_(n)} وحدات` : `في ${n_(n)} وحدة`),
  // How many times it was raised (distinct stays — one review per stay) beside the units, founder 8 Oct: the bar
  // alone made two problems on the same number of units look equal.
  topTimes: (n) => counted(n, { one: 'مرة واحدة', two: 'مرتان', few: 'مرات', many: 'مرة' }),
  moreUnits: (n) => `+${n_(n)}`,
  fleetProblemTip: (name, units, stays) => `${name}: ${TEXT.stays(stays)} ${TEXT.topUnits(units)}`,
  fleetPraiseTip: (name, reviews) => `${name}: في ${reviewsAfterIn(reviews)}`,
  improveTitle: 'ما يزعج الضيوف',
  improveHint: 'كل سطر مشكلة ذكرها الضيوف في هذه الوحدة. الأهم في الأعلى.',
  improveEmpty: 'لم يذكر الضيوف أي مشكلة في هذه الوحدة.',
  loveTitle: 'ما يحبه الضيوف',
  loveEmpty: 'لا توجد إشادات بعد في تقييمات هذه الوحدة.',

  badgeRepeat: 'متكرر',
  badgeHigh: 'عاجل',
  badgeCameBack: 'عاد بعد الإصلاح',
  badgeHandled: 'شكوى مفتوحة',
  stays: (n) => counted(n, { one: 'إقامة واحدة', two: 'إقامتان', few: 'إقامات', many: 'إقامة' }),
  lastMention: (date) => `آخر ذكر ${date}`,
  reviewsCount: (n) => counted(n, { one: 'تقييم واحد', two: 'تقييمان', few: 'تقييمات', many: 'تقييمًا' }),
  reviewsWord: (n) => (n === 1 ? 'تقييم' : n === 2 ? 'تقييمان' : n <= 10 ? 'تقييمات' : 'تقييمًا'),
  privateNote: 'ملاحظة خاصة',
  privateNoteHint: 'من ملاحظة الضيف الخاصة: للفريق فقط، ولا تُشارك مع المالك',
  airbnbLabel: 'اختيار في Airbnb',
  airbnbLabelHint: 'اختاره الضيف من قائمة Airbnb «ما يجب تحسينه»: للفريق فقط، ولا يُشارك مع المالك',
  showMore: (n) => `عرض ${n_(n)} أخرى`,
  showLess: 'إخفاء',
  viewComplaint: 'عرض الشكوى',
  noStay: 'لا حجز مرتبط',
  noStayHint: 'لا يمكن فتح شكوى: لا يوجد حجز مرتبط بهذا التقييم',
  score: (s) => `${n_(s)} من ١٠`
};
